'use client';

/**
 * UI-003 / UI-004 — modal-layer behaviour shared by every overlay that blocks the page.
 *
 * WAI-ARIA APG Dialog (Modal). While `open`, the hook owns a portal container on <body>
 * (returned as `host`; the overlay renders into it) and:
 *   - moves focus into the dialog — the panel by default, or `initialFocus` — traps Tab in
 *     it, and returns focus on close (`returnFocus`, else whatever was focused at open);
 *   - makes every other child of <body> `inert` — the app root, and with it the fixed
 *     header — except overlay layers marked `data-overlay-layer="alert"` (toasts, see
 *     AlertLayer);
 *   - closes on Escape (topmost layer only) through `onClose('escape')`, unless
 *     `closeOnEscape` is false or the dialog is not `dismissible` (e.g. a save is in flight);
 *   - scroll-locks the page without a sideways shift, and restores its scroll offset.
 *
 * Focus lands on the PANEL by default, for UI-001's reason: a focused button or field would
 * catch a keyboard-wedge scanner's keystrokes and terminating Enter. A dialog whose job is
 * to take a scan (or typing) names that field as `initialFocus` instead (UI-004 D3).
 *
 * Keys: Escape is taken on a window capture listener and stopped there while a layer is
 * open, so page and header Escape handlers (Header menus, per-dialog listeners) cannot run
 * twice. Tab is handled at document bubble and skipped if a handler inside the dialog
 * already prevented it. Every other key passes untouched — the GS separator keystrokes a
 * scan depends on (Ctrl+], Alt+numpad 0029, 0x1D; SCAN-001) are never seen here.
 */

import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Marks a portal container on <body>; "alert" layers stay live above a modal. */
export const OVERLAY_LAYER_ATTR = 'data-overlay-layer';

export type CloseReason = 'escape' | 'backdrop';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Body children that are never made inert: not content, or Next's live announcer. */
const NEVER_INERT = new Set([
  'SCRIPT',
  'STYLE',
  'LINK',
  'NOSCRIPT',
  'TEMPLATE',
  'NEXT-ROUTE-ANNOUNCER',
  'NEXTJS-PORTAL',
]);

/** Open layers, oldest first. Only the last one handles Escape and the Tab trap. */
const stack: HTMLElement[] = [];
let scrollLocks = 0;
let scrollRestore: (() => void) | null = null;

/**
 * True while any modal layer is open. Page-level shortcuts (Header Ctrl+1 / Ctrl+2) check
 * this and do nothing: `inert` stops clicks and focus, not document key listeners.
 */
export function isModalLayerOpen(): boolean {
  return stack.length > 0;
}

/**
 * Put `data-focus-key="<stable id>"` on a dialog's trigger when the list it sits in may be
 * re-rendered while the dialog is open: focus then returns to the re-rendered trigger.
 */
export const FOCUS_KEY_ATTR = 'data-focus-key';

/**
 * The element carrying `attr="value"`, for `returnFocus` when the trigger may have been
 * re-rendered (a list refetch remounts its rows) or the dialog was opened by a row click.
 */
export function findFocusTarget(attr: string, value: string): HTMLElement | null {
  const el = Array.from(document.querySelectorAll<HTMLElement>(`[${attr}]`)).find(
    (e) => e.getAttribute(attr) === value
  );
  return el ?? null;
}

function lockScroll() {
  if (scrollLocks++ > 0) return;
  const { body } = document;
  const prevOverflow = body.style.overflow;
  const prevPaddingRight = body.style.paddingRight;
  const restoreScrollY = window.scrollY;
  // overflow:hidden on <body> propagates to the viewport; the measured gutter replaces
  // the width the vanishing scrollbar gave up, so nothing shifts sideways.
  const gutter = window.innerWidth - document.documentElement.clientWidth;
  body.style.overflow = 'hidden';
  if (gutter > 0) {
    const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${current + gutter}px`;
  }
  scrollRestore = () => {
    body.style.overflow = prevOverflow;
    body.style.paddingRight = prevPaddingRight;
    window.scrollTo({ top: restoreScrollY, behavior: 'auto' });
  };
}

function unlockScroll() {
  if (--scrollLocks > 0) return;
  scrollLocks = 0;
  scrollRestore?.();
  scrollRestore = null;
}

/** Make every other <body> child inert; returns the undo. Already-inert ones are left alone. */
function inertSiblings(host: HTMLElement): () => void {
  const made: Element[] = [];
  for (const el of Array.from(document.body.children)) {
    if (el === host || NEVER_INERT.has(el.tagName)) continue;
    const layer = el.getAttribute(OVERLAY_LAYER_ATTR);
    if (layer === 'alert') continue;
    // A modal layer appended after this one was opened after it and sits above it.
    if (layer === 'modal' && host.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)
      continue;
    if (el.hasAttribute('inert')) continue;
    el.setAttribute('inert', '');
    made.push(el);
  }
  return () => {
    for (const el of made) el.removeAttribute('inert');
  };
}

function isShown(el: HTMLElement): boolean {
  if (el.closest('[hidden],[inert]')) return false;
  const style = window.getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isShown);
}

export interface ModalLayerOptions {
  open: boolean;
  /** The dialog panel: bounds the Tab trap, and takes focus unless `initialFocus` is set. */
  panelRef: RefObject<HTMLElement | null>;
  /** Escape (and, in the components, a backdrop click) asks to close; the caller decides. */
  onClose: (reason: CloseReason) => void;
  /** Default true. False: Escape does nothing (a one-time secret, closing discards a scan). */
  closeOnEscape?: boolean;
  /** Default true. False while a request is in flight: Escape and backdrop do nothing. */
  dismissible?: boolean;
  /** Focus this on open instead of the panel (a scan field, a dialog's only input). */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * Where focus goes on close. Defaults to whatever was focused at open. Return null to
   * fall back to that default (e.g. the trigger has since unmounted).
   */
  returnFocus?: () => HTMLElement | null;
}

/** Returns the portal container to render the overlay into, or null while closed. */
export function useModalLayer({
  open,
  panelRef,
  onClose,
  closeOnEscape = true,
  dismissible = true,
  initialFocus,
  returnFocus,
}: ModalLayerOptions): HTMLElement | null {
  const [host, setHost] = useState<HTMLElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  // Callers pass inline arrows and flip `dismissible` while saving; reading them through
  // refs keeps the layer effect from re-running (and re-stealing focus) on every render.
  const latest = useRef({ onClose, closeOnEscape, dismissible, initialFocus, returnFocus });
  latest.current = { onClose, closeOnEscape, dismissible, initialFocus, returnFocus };

  // 1. On open: note the opener BEFORE the dialog's content mounts (an autoFocus field
  //    inside would otherwise already hold focus), then create the container.
  useLayoutEffect(() => {
    if (!open) return;
    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = document.createElement('div');
    el.setAttribute(OVERLAY_LAYER_ATTR, 'modal');
    document.body.appendChild(el);
    setHost(el);
    return () => {
      setHost(null);
      el.remove();
    };
  }, [open]);

  // 2. Once the content is in the container: stack, lock, inert, focus, keys.
  //    Layout effect, so focus moves before the browser paints or a scan keystroke lands.
  //    Safe under StrictMode's double run: the cleanup undoes exactly what the body did,
  //    and the container itself belongs to effect 1.
  useLayoutEffect(() => {
    if (!host) return;
    const opener = openerRef.current;

    stack.push(host);
    lockScroll();
    const undoInert = inertSiblings(host);

    const panel = panelRef.current;
    const wanted = latest.current.initialFocus?.current ?? null;
    wanted?.focus({ preventScroll: true });
    // A disabled or hidden initialFocus target does not take focus; the panel does.
    if (!wanted || document.activeElement !== wanted) panel?.focus({ preventScroll: true });

    const isTop = () => stack[stack.length - 1] === host;

    const onEscapeCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.isComposing || !isTop()) return;
      e.stopPropagation();
      const { closeOnEscape: canEscape, dismissible: canDismiss } = latest.current;
      if (canEscape && canDismiss) latest.current.onClose('escape');
    };

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.defaultPrevented || !isTop()) return;
      const p = panelRef.current;
      if (!p) return;
      const focusable = focusableWithin(p);
      if (focusable.length === 0) {
        e.preventDefault();
        p.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // Right after open the panel itself is focused; Shift+Tab from there wraps to the end.
      if (e.shiftKey && (active === first || active === p || !p.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !p.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };

    // Belt and braces for browsers without `inert`: focus that escapes is pulled back.
    const onFocusIn = (e: FocusEvent) => {
      if (!isTop()) return;
      const p = panelRef.current;
      const target = e.target as Node | null;
      if (!p || !target || p.contains(target)) return;
      if (target instanceof Element && target.closest(`[${OVERLAY_LAYER_ATTR}="alert"]`)) return;
      p.focus({ preventScroll: true });
    };

    window.addEventListener('keydown', onEscapeCapture, true);
    document.addEventListener('keydown', onTab);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      window.removeEventListener('keydown', onEscapeCapture, true);
      document.removeEventListener('keydown', onTab);
      document.removeEventListener('focusin', onFocusIn);
      const i = stack.lastIndexOf(host);
      if (i >= 0) stack.splice(i, 1);
      // Order matters: an inert element cannot take focus, so release the page first.
      undoInert();
      unlockScroll();
      let target = latest.current.returnFocus?.() ?? opener;
      // A list refetch re-renders the row the dialog was opened from: the opener element is
      // gone, but its replacement carries the same `data-focus-key` (UI-004 G2).
      if (target && !target.isConnected) {
        const key = target.getAttribute(FOCUS_KEY_ATTR);
        target = key ? findFocusTarget(FOCUS_KEY_ATTR, key) : null;
      }
      if (target && target.isConnected) target.focus({ preventScroll: true });
    };
    // panelRef is a stable ref object; everything else is read through `latest`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host]);

  return open ? host : null;
}
